import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { DynamicIcon } from '@lumapps/lumx-images/components/DynamicIcon';
import {
    Tooltip,
    Typography,
    Text,
    ColorVariant,
    type ColorPalette,
    Size,
    FlexBox,
    Orientation,
    Alignment,
} from '@lumapps/lumx/react';

import './index.scss';

export interface UserDetailedFieldProps {
    /**
     * The value to display.
     */
    value: React.ReactNode;
    /**
     * The icon name to display next to the value.
     */
    icon?: string;
    /**
     * The label describing the field. Used as the tooltip for the value.
     */
    label: string;
    /**
     * The label for the icon. Used as the tooltip for the icon.
     */
    iconLabel?: string;
    /**
     * The color palette variant to use for the text and icon.
     */
    color: ColorPalette;
    /** Custom classname */
    className?: string;
}

const CLASSNAME = 'lumx-user-detailed-field';

/**
 * Displays a single detail field for a user, including an optional icon and label with tooltips.
 * It renders the value with its corresponding label as a tooltip.
 *
 * @param {UserDetailedFieldProps}
 * @returns UserDetailedField
 */
export const UserDetailedField: React.FC<UserDetailedFieldProps> = ({
    label,
    iconLabel,
    value,
    color,
    icon,
    className,
}) => {
    const { block, element } = useClassnames(CLASSNAME);

    if (!value) {
        return null;
    }

    return (
        <FlexBox
            gap={Size.regular}
            orientation={Orientation.horizontal}
            hAlign={Alignment.center}
            className={block([className])}
        >
            {icon && iconLabel ? (
                <Text as="p" typography={Typography.body1}>
                    <Tooltip label={iconLabel}>
                        <DynamicIcon alt={iconLabel} icon={icon} color={color} colorVariant={ColorVariant.L2} />
                    </Tooltip>
                </Text>
            ) : null}

            <Tooltip label={label}>
                <Text
                    as="p"
                    typography={Typography.body1}
                    color={color}
                    colorVariant={ColorVariant.L2}
                    className={element('value')}
                >
                    {value}
                </Text>
            </Tooltip>
        </FlexBox>
    );
};
